import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Typography, Timeline, Card, Modal, Table, Spin, message } from "antd";
import { FilePdfOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, RightOutlined, CloseOutlined } from "@ant-design/icons";
import { getOrderDetail, getOrderAuditRecord, downloadOrderFormPdf, ORDER_STATUS_DICT, OrderStatus, useMetadataStore } from "@ifa/utils";
import { FilePreviewModal } from "../components/FilePreviewModal";
import CustomButton from "../components/CustomButton";
import type { FlowRecord, OrderChangeVO } from "@ifa/utils";
import dayjs from "dayjs";
import styles from "./OrderForm.module.css";
import copyIcon from "../assets/svg/copy.svg";
import { getOrderChangeHistoryWithFallback } from "../services/orderChangeHistory";
import { isZuuFnaVariant } from "./order-fna/fnaVariant.js";

const { Text, Title } = Typography;

export const OrderAuditLog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams<{ orderId: string }>();
  const fnaVersion = useMetadataStore((state) => state.fnaVersion);
  const isZuuMode = isZuuFnaVariant({ fnaVersion });

  const [orderData, setOrderData] = useState<any>(null);
  const [records, setRecords] = useState<FlowRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [changeHistory, setChangeHistory] = useState<OrderChangeVO[]>([]);
  const [modalLoading, setModalLoading] = useState(false);

  // File Preview Modal State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string>("");
  const [previewFileName, setPreviewFileName] = useState<string>("");

  useEffect(() => {
    if (orderId) {
      setLoading(true);
      Promise.all([getOrderDetail(Number(orderId)), getOrderAuditRecord(Number(orderId))])
        .then(([orderRes, auditRes]) => {
          setOrderData(orderRes);
          if (auditRes && auditRes.flowRecords) {
            // Sort by createTime desc
            const sorted = [...auditRes.flowRecords].sort((a, b) => {
              return dayjs(b.createTime).valueOf() - dayjs(a.createTime).valueOf();
            });
            setRecords(sorted);
          }
        })
        .catch((err) => {
          console.error(err);
          // message.error("獲取數據失敗");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [orderId]);

  const handleBack = () => {
    // Check if we came from correction-list
    if (location.state?.from === "correction") {
      navigate(`/orders/${orderId}/correction`);
    } else {
      navigate(-1);
    }
  };

  const handleViewChanges = async (record: FlowRecord) => {
    if (!orderId || !record.flowRecordId) return;

    setModalLoading(true);
    setIsModalOpen(true);
    setChangeHistory([]);

    try {
      const res = await getOrderChangeHistoryWithFallback(Number(orderId), record.flowRecordId);
      // Handle potential response variations (Array vs Single Object)
      const data = Array.isArray(res) ? res : [res];
      setChangeHistory(data as OrderChangeVO[]);
    } catch (err) {
      console.error(err);
      // message.error("獲取批改內容失敗");
    } finally {
      setModalLoading(false);
    }
  };

  const handlePreviewFile = async (record: FlowRecord) => {
    if (!orderId || !record.lastModifiedFlowRecordId) {
      // message.error("預覽文件失敗");
      return;
    }
    try {
      const result = await downloadOrderFormPdf({
        orderId: Number(orderId),
        formType: "SUPPLEMENTARY_INFORMATION_FORM",
        flowRecordId: record.lastModifiedFlowRecordId,
      });
      if (result && result.blob) {
        const url = URL.createObjectURL(result.blob);
        setPreviewFileUrl(url);
        setPreviewFileName(result.filename || "批改檔案.pdf");
        setPreviewOpen(true);
      }
    } catch {
      // message.error("預覽文件失敗");
    }
  };

  const renderTimelineItem = (record: FlowRecord) => {
    let color = "#cbd5e1";
    let dot = <ClockCircleOutlined style={{ fontSize: "16px", color: "#64748b" }} />;

    if (record.newOrderAuditStatus?.includes("REJECT")) {
      color = "#ef4444";
      dot = <CloseCircleOutlined style={{ fontSize: "16px", color: "#ef4444" }} />;
    } else if (record.newOrderAuditStatus?.includes("APPROVED") || record.newOrderAuditStatus === "CONFIRMATION_PENDING_REVIEW") {
      color = "#10b981";
      dot = <CheckCircleOutlined style={{ fontSize: "16px", color: "#10b981" }} />;
    }

    return {
      color,
      dot,
      children: (
        <Card
          style={{
            marginBottom: 20,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
          }}
          styles={{ body: { padding: "16px 20px" } }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 16 }}>
            <Text strong style={{ fontSize: 16, color: "#0f172a", fontWeight: 600, flex: 1, wordBreak: "break-word", minWidth: 0 }}>
              {record.description || "狀態變更"}
            </Text>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
              <Text type="secondary" style={{ fontSize: 13, color: "#64748b", whiteSpace: "nowrap" }}>
                {record.creatorName}
              </Text>
              <Text type="secondary" style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
                {record.createTime}
              </Text>
            </div>
          </div>

          {(record.canViewChangeHistory || record.signature) && (
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              {record.canViewChangeHistory && (
                <CustomButton
                  bgColor="transparent"
                  color="var(--theme-primary)"
                  height={36}
                  fontSize={14}
                  style={{
                    border: "1px solid var(--theme-primary)",
                    borderRadius: 6,
                  }}
                  onClick={() => handleViewChanges(record)}
                >
                  查詢批改內容
                </CustomButton>
              )}

              {record.signature && !isZuuMode && (
                <CustomButton
                  icon={<FilePdfOutlined style={{ color: "#ef4444" }} />}
                  bgColor="#f8fafc"
                  color="#475569"
                  height={36}
                  fontSize={14}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                  }}
                  onClick={() => handlePreviewFile(record)}
                >
                  查看檔案
                </CustomButton>
              )}
            </div>
          )}
        </Card>
      ),
    };
  };

  // 渲染支持换行的文本
  const renderTextWithLineBreaks = (text: string) => {
    if (!text) return "-";
    return (
      <>
        {text.split(/\\n|\n/).map((line, index, array) => (
          <React.Fragment key={index}>
            {line}
            {index < array.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    );
  };

  const columns = [
    { title: "變更項目", dataIndex: "fieldName", key: "fieldName", width: "30%" },
    { 
      title: "變更前", 
      dataIndex: "oldValue", 
      key: "oldValue", 
      width: "35%",
      render: renderTextWithLineBreaks
    },
    { 
      title: "變更後", 
      dataIndex: "newValue", 
      key: "newValue", 
      width: "35%",
      render: renderTextWithLineBreaks
    },
  ];

  // Flatten data for table
  const tableData = changeHistory.flatMap((vo, idx) =>
    (vo.changeDetails || []).map((detail, dIdx) => ({
      ...detail,
      key: `${idx}-${dIdx}`,
    }))
  );

  return (
    <div className={styles["order-form-container"]} style={{ height: "100vh", flexDirection: "column", background: "#f5f5f5" }}>
      <div className={styles["sticky-header-container"]}>
        <header className={styles["order-header"]}>
          <div className={styles["header-left"]}>
            <div className={styles["header-title"]}>
              <span className={styles["header-breadcrumb"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer" }}>
                訂單
              </span>
              <RightOutlined className={styles["header-separator"]} style={{ fontSize: "12px" }} />
              <span>訂單紀錄/批改訂單/審核批改紀錄</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            {/* Order Info */}
            {orderData && (
              <div className={styles["header-info"]}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                  <span className={`${styles["status-tag"]} ${styles[`status-${orderData.orderStatus || OrderStatus.Draft}`]}`}>
                    {ORDER_STATUS_DICT[orderData.orderStatus as OrderStatus] || "草稿"}
                  </span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className={styles["info-item"]} style={{ fontSize: 12 }}>
                      <span className={styles["info-label"]}>編號:</span> {orderData.orderNo || "-"}
                    </span>
                    {orderData.orderNo && (
                      <img
                        src={copyIcon}
                        alt="copy"
                        style={{ marginLeft: 4, cursor: "pointer", width: 12, height: 12 }}
                        onClick={() => {
                          navigator.clipboard.writeText(orderData.orderNo || "");
                          message.success("已複製");
                        }}
                      />
                    )}
                  </div>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", margin: "0 24px" }} />
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span className={styles["info-item"]} style={{ fontSize: 12, lineHeight: "14px", color: "#999" }}>
                    投保人 / 受保人 :
                  </span>
                  <span className={styles["info-item"]} style={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, color: "#333" }}>
                    {orderData.policyholder?.basic?.chineseSurname || "-"} {orderData.policyholder?.basic?.chineseGivenName || "-"} <span style={{ color: "#E8E8E8", margin: "0 8px" }}>/</span>{" "}
                    {orderData.insured?.basic?.chineseSurname || "-"} {orderData.insured?.basic?.chineseGivenName || "-"}
                  </span>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", marginLeft: 24 }} />
              </div>
            )}
            <div className={styles["header-close"]} onClick={handleBack} style={{ marginLeft: 16, cursor: "pointer" }}>
              <CloseOutlined style={{ fontSize: 18, color: "#999" }} />
            </div>
          </div>
        </header>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "24px 20px" }}>
        {orderData && orderData.orderStatus === "PENDING_CONFIRMATION" && (
          <div
            style={{
              background: "#FFFBE6",
              padding: "12px 16px",
              marginBottom: 16,
              border: "1px solid #FFE58F",
              borderRadius: 8,
              color: "#FAAD14",
              textAlign: "center",
              fontSize: 14,
            }}
          >
            訂單審核 - 客戶已確認修改內容無誤，待審核員確認
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
            訂單審核及批改記錄
          </Title>
          <CustomButton
            bgColor="transparent"
            color="var(--theme-primary)"
            height={36}
            fontSize={14}
            style={{
              border: "1px solid var(--theme-primary)",
              borderRadius: 6,
            }}
            onClick={() =>
              navigate(`/orders/${orderId}`, {
                state: {
                  from: "audit-log",
                  orderStatus: orderData?.orderStatus,
                  orderAuditStatus: orderData?.orderAuditStatus,
                },
              })
            }
          >
            查閱訂單詳情
          </CustomButton>
        </div>

        <div style={{ padding: "0 8px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 50 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Timeline items={records.map(renderTimelineItem)} />
          )}
        </div>
      </div>

      <Modal
        title="批改內容詳情"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
        styles={{
          header: { borderBottom: "1px solid #e2e8f0", paddingBottom: 16 },
          body: { paddingTop: 16 },
        }}
      >
        {modalLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Table dataSource={tableData} columns={columns} pagination={false} scroll={{ y: 400 }} locale={{ emptyText: "無變更內容" }} />
        )}
      </Modal>

      <FilePreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          if (previewFileUrl) {
            URL.revokeObjectURL(previewFileUrl);
          }
        }}
        fileUrl={previewFileUrl}
        fileName={previewFileName}
        fileType={"pdf"}
      />
    </div>
  );
};
